import { COUNCIL_REPORT_PAGE_SLUGS } from '@fixtyer/content';
import { z } from 'zod';

export const GUEST_DRAFT_STORAGE_KEY = 'fixtyer_guest_claim_draft';
export const GUEST_DRAFT_VERSION = 1 as const;
export const GUEST_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const damageCategories = [
  'tyre',
  'wheel',
  'suspension',
  'bodywork',
  'steering',
  'other',
] as const;

const MINIMUM_INCIDENT_DATE = Date.UTC(1900, 0, 1);

export function isValidIncidentDate(
  value: string,
  now = Date.now()
): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = Date.UTC(year, month - 1, day);
  const date = new Date(timestamp);
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return false;
  }

  const current = new Date(now);
  const currentUtcDate = Date.UTC(
    current.getUTCFullYear(),
    current.getUTCMonth(),
    current.getUTCDate()
  );
  return timestamp >= MINIMUM_INCIDENT_DATE && timestamp <= currentUtcDate;
}

const guestDraftSchema = z.object({
  version: z.literal(GUEST_DRAFT_VERSION),
  createdAt: z.number().int().nonnegative(),
  incidentDate: z.string().refine((value) => isValidIncidentDate(value)),
  damageCategory: z.enum(damageCategories),
  locationPostcode: z.string().regex(/^[A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2}$/),
  councilHint: z.string().optional(),
}).strict().superRefine((draft, context) => {
  if (
    draft.councilHint &&
    !COUNCIL_REPORT_PAGE_SLUGS.includes(draft.councilHint)
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['councilHint'],
      message: 'Unknown council hint',
    });
  }
});

export type GuestDraft = z.infer<typeof guestDraftSchema>;
type GuestDraftInput = Omit<GuestDraft, 'version' | 'createdAt'>;
type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

let inMemoryDraft: GuestDraft | null = null;

function defaultStorage(): StorageLike | undefined {
  try {
    return typeof sessionStorage === 'undefined' ? undefined : sessionStorage;
  } catch {
    return undefined;
  }
}

export function createGuestDraft(
  input: GuestDraftInput,
  now = Date.now()
): GuestDraft {
  return guestDraftSchema.parse({
    version: GUEST_DRAFT_VERSION,
    createdAt: now,
    ...input,
  });
}

export function saveGuestDraft(
  draft: GuestDraft,
  storage: StorageLike | undefined = defaultStorage()
): boolean {
  const validated = guestDraftSchema.parse(draft);
  inMemoryDraft = validated;
  try {
    if (!storage) return false;
    storage.setItem(GUEST_DRAFT_STORAGE_KEY, JSON.stringify(validated));
    return true;
  } catch {
    return false;
  }
}

export function loadGuestDraft(
  storage: StorageLike | undefined = defaultStorage(),
  now = Date.now()
): GuestDraft | null {
  try {
    const raw = storage?.getItem(GUEST_DRAFT_STORAGE_KEY);
    if (raw) {
      const parsed = guestDraftSchema.safeParse(JSON.parse(raw));
      if (
        parsed.success &&
        now >= parsed.data.createdAt &&
        now - parsed.data.createdAt <= GUEST_DRAFT_MAX_AGE_MS
      ) {
        inMemoryDraft = parsed.data;
        return parsed.data;
      }
      storage?.removeItem(GUEST_DRAFT_STORAGE_KEY);
      inMemoryDraft = null;
      return null;
    }
  } catch {
    return inMemoryDraft &&
      now >= inMemoryDraft.createdAt &&
      now - inMemoryDraft.createdAt <= GUEST_DRAFT_MAX_AGE_MS
      ? inMemoryDraft
      : null;
  }
  return inMemoryDraft &&
    now >= inMemoryDraft.createdAt &&
    now - inMemoryDraft.createdAt <= GUEST_DRAFT_MAX_AGE_MS
    ? inMemoryDraft
    : null;
}

export function clearGuestDraft(
  storage: StorageLike | undefined = defaultStorage()
): void {
  inMemoryDraft = null;
  try {
    storage?.removeItem(GUEST_DRAFT_STORAGE_KEY);
  } catch {
    // The in-memory fallback has still been cleared.
  }
}

export function guestDraftToClaimDefaults(draft: GuestDraft) {
  return {
    incidentDate: draft.incidentDate,
    damageType: [draft.damageCategory],
    locationPostcode: draft.locationPostcode,
  };
}

function isEmpty(value: unknown): boolean {
  return value == null || value === '' || (Array.isArray(value) && value.length === 0);
}

export function mergeGuestDefaults<T extends Record<string, unknown>>(
  current: T,
  defaults: Partial<T>
): T {
  const merged = { ...current };
  for (const [key, value] of Object.entries(defaults)) {
    if (isEmpty(merged[key])) {
      merged[key as keyof T] = value as T[keyof T];
    }
  }
  return merged;
}

export function isSafeContinuation(value: string | null | undefined): string {
  if (value === '/claims/new' || value === '/claims/new?restored=1') return value;
  return '/claims/new';
}

const councilSlugs = new Set(COUNCIL_REPORT_PAGE_SLUGS);

export function readCouncilHint(search: string): string | null {
  const slug = new URLSearchParams(search).get('council');
  return slug && councilSlugs.has(slug) ? slug : null;
}

export type GuestDamageCategory = GuestDraft['damageCategory'];
